/**
 * Which conversation is live, as one authority both sides read.
 *
 * There were two. `useHfclChat` kept a `sessionIdRef` and swapped it in
 * `resetChat`; `AgentBridgeProvider` kept an `activeSessionRef` and set it
 * from whatever response happened to arrive. Nothing connected them, so a
 * reply that was already in flight when the user pressed reset would land
 * at the provider, write its own - now superseded - session id into the
 * provider's reference, and thereby declare itself current. Every command
 * that followed was then dispatched against a conversation the user had
 * already left.
 *
 * The fix is not another guard. It is that "which conversation is live" has
 * one answer, written only by the lifecycle events that change it - a
 * reset, a thread switch - and read by everything else.
 *
 * A generation counter rides alongside the id, because ids can repeat: a
 * thread switched away from and back to is the same id and a different
 * stretch of work, and anything issued before the switch is stale even
 * though the id matches.
 */

let liveSessionId = null;
let generation = 0;

// Work that has not finished yet, so a reset can stop it rather than
// waiting for it to notice. Held as abort controllers keyed by the
// generation that created them.
const outstanding = new Set();
const resetListeners = new Set();

/**
 * The conversation currently on screen.
 * @return {{sessionId: (string|null), generation: number}} live session
 */
export function liveSession() {
	return {sessionId: liveSessionId, generation};
}

/**
 * Adopt a session without treating it as a change of conversation.
 *
 * Called when a session id is first established. Does not bump the
 * generation: nothing is stale, there is simply now an answer.
 *
 * @param {string} sessionId session identifier
 * @return {number} the current generation
 */
export function adoptSession(sessionId) {
	if (sessionId && liveSessionId !== sessionId) liveSessionId = sessionId;
	return generation;
}

/**
 * Move to a different conversation, immediately.
 *
 * Immediately is the whole point: everything issued before this returns is
 * stale from this moment, whether or not it has noticed.
 *
 * @param {string|null} sessionId the conversation now on screen
 * @return {number} the new generation
 */
export function switchSession(sessionId) {
	liveSessionId = sessionId ?? null;
	generation += 1;
	cancelOutstanding('session_switched');
	resetListeners.forEach((listener) => {
		try {
			listener({sessionId: liveSessionId, generation});
		} catch (_) { /* a listener that throws must not stop the reset */ }
	});
	return generation;
}

/**
 * Whether work issued for this session and generation is still current.
 *
 * Both halves are required. The id alone cannot tell a thread switched
 * away from and back to apart from one never left.
 *
 * @param {string|null|undefined} sessionId session the work belongs to
 * @param {number|null|undefined} atGeneration generation it was issued in
 * @return {boolean} true when the work is still current
 */
export function isLive(sessionId, atGeneration) {
	if (atGeneration !== undefined && atGeneration !== null &&
		atGeneration !== generation) return false;
	if (!sessionId || !liveSessionId) return true;
	return sessionId === liveSessionId;
}

/**
 * Register in-flight work so a reset can cancel it.
 * @param {AbortController} controller work to cancel on reset
 * @return {Function} release callback
 */
export function holdOutstanding(controller) {
	outstanding.add(controller);
	return () => outstanding.delete(controller);
}

/**
 * Stop everything still running.
 * @param {string} why reason recorded on the abort
 * @return {number} how many were cancelled
 */
export function cancelOutstanding(why = 'cancelled') {
	const held = [...outstanding];
	outstanding.clear();
	held.forEach((controller) => {
		try {
			controller.abort(new Error(why));
		} catch (_) { /* already aborted */ }
	});
	return held.length;
}

/**
 * Be told when the conversation changes.
 * @param {Function} listener called with the new session
 * @return {Function} unsubscribe callback
 */
export function onSessionChange(listener) {
	resetListeners.add(listener);
	return () => resetListeners.delete(listener);
}

/**
 * Forget everything. For tests, and for a signed-out page.
 * @return {void}
 */
export function resetLifecycleForTests() {
	liveSessionId = null;
	generation = 0;
	outstanding.clear();
	resetListeners.clear();
}
